import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Menu, X, ChevronDown, MessageCircle, MapPin, Facebook, Instagram, Youtube, Linkedin, BadgeCheck, PhoneCall, PhoneOutgoing, Loader2, Send, Lock } from "lucide-react";
import { SITE, SERVICE_LINKS, CITY_LINKS, PROMISES } from "../data/site";
import { services as ALL_SERVICES, SERVICE_GROUPS } from "../data/services";
import { brands as BRAND_LINKS } from "../data/brands";
import { trackMetaLead } from "../lib/metaPixel";
import { getAttribution, dl, sha256Hex } from "../lib/tracking";

import { API } from "@/lib/api";

// Zwei klare Handlungswege überall: Anrufen + Rückruf (WhatsApp als dritte,
// kleinere Option). Das Rückruf-Modal öffnet global über dieses Event.
export const openRueckrufModal = () => window.dispatchEvent(new CustomEvent("ue24:rueckruf"));

const GlobalRueckruf = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onOpen = () => setShow(true);
    window.addEventListener("ue24:rueckruf", onOpen);
    return () => window.removeEventListener("ue24:rueckruf", onOpen);
  }, []);
  return show ? <RueckrufModal onClose={() => setShow(false)} /> : null;
};

export const Logo = ({ light }) => (
  <Link to="/" className="flex items-center shrink-0" data-testid="logo-link" aria-label="UnfallExpert24 Startseite">
    {light ? (
      <span className="bg-white rounded-xl px-3 py-1.5 inline-flex">
        <img src="/logo.png" alt="UnfallExpert24 – KFZ-Sachverständige" width="480" height="131" className="h-8 w-auto" />
      </span>
    ) : (
      <img src="/logo.png" alt="UnfallExpert24 – KFZ-Sachverständige" width="480" height="131" className="h-12 sm:h-14 lg:h-16 w-auto" />
    )}
  </Link>
);

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

const SOCIALS = [
  { key: "facebook", label: "Facebook", url: SITE.facebook, Icon: Facebook },
  { key: "instagram", label: "Instagram", url: SITE.instagram, Icon: Instagram },
  { key: "youtube", label: "YouTube", url: SITE.youtube, Icon: Youtube },
  { key: "linkedin", label: "LinkedIn", url: SITE.linkedin, Icon: Linkedin },
  { key: "tiktok", label: "TikTok", url: SITE.tiktok, Icon: TikTokIcon },
];

const SocialLinks = ({ size = 13, className = "", testidPrefix = "social" }) => (
  <>
    {SOCIALS.map(({ key, label, url, Icon }) => (
      <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={`UnfallExpert24 auf ${label}`} data-testid={`${testidPrefix}-${key}`} className={className}><Icon size={size} /></a>
    ))}
  </>
);

const NAV = [
  { to: "/leistungen/", label: "Leistungen", mega: true },
  { to: "/automarken/", label: "Automarken", dropdown: [...BRAND_LINKS.slice(0, 10).map((b) => ({ to: `/automarken/kfz-gutachten-${b.slug}`, label: b.name })), { to: "/schadenbeispiele/", label: "Schadenbeispiele" }] },
  { to: "/standorte", label: "Standorte", dropdown: CITY_LINKS.slice(0, 8).map((c) => ({ to: `/kfz-gutachter-${c.slug}`, label: c.name })) },
  { to: "/ratgeber/", label: "Wissen", dropdown: [
    { to: "/ratgeber/", label: "Ratgeber" },
    { to: "/ihr-recht", label: "Ihr Recht" },
    { to: "/faq", label: "Häufige Fragen" },
    { to: "/glossar", label: "Glossar" },
    { to: "/unfall", label: "Unfallthemen" },
    { to: "/schadenarten", label: "Schadenarten" },
    { to: "/downloads", label: "Downloads" },
  ] },
  { to: "/rechner", label: "Rechner", dropdown: [
    { to: "/rechner/nutzungsausfall", label: "Nutzungsausfall-Rechner" },
    { to: "/rechner/wertminderung", label: "Wertminderungs-Rechner" },
    { to: "/rechner/totalschaden", label: "Totalschaden-Rechner" },
    { to: "/rechner/haftungsquote", label: "Haftungsquote-Rechner" },
  ] },
  { to: "/ueber-uns/", label: "Über uns", dropdown: [
    { to: "/ueber-uns/", label: "Über uns" },
    { to: "/team", label: "Unser Team" },
    { to: "/qualitaet", label: "Qualität & Standards" },
    { to: "/technik", label: "Technik & Ausstattung" },
  ] },
  { to: "/kontakt", label: "Kontakt" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const location = useLocation();
  useEffect(() => { setOpen(false); setExpanded(null); }, [location.pathname]);

  return (
    <header className="fixed top-0 inset-x-0 z-50" data-testid="site-header">
      <div className="hidden md:block bg-navy text-slate-200 text-xs" data-testid="header-infobar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-9 flex items-center justify-between">
          <span>KFZ-Gutachter Köln, Krefeld & Moers · {PROMISES.erreichbarkeit} · <a href={SITE.phoneHref} className="font-bold text-white hover:text-ember">{SITE.phone}</a></span>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5">
              <SocialLinks size={13} testidPrefix="infobar" className="p-1.5 rounded-full bg-white/10 hover:bg-ember hover:text-white transition-colors" />
            </span>
            <a href={SITE.phoneHref} data-testid="infobar-call-button" className="cta-gradient flex items-center gap-1.5 px-4 py-1 rounded-full font-bold text-white"><Phone size={12} /> Jetzt anrufen</a>
            <button onClick={openRueckrufModal} data-testid="infobar-callback-button" className="flex items-center gap-1.5 px-4 py-1 rounded-full font-bold text-white border border-white/30 hover:bg-white/10 transition-colors"><PhoneOutgoing size={12} /> Rückruf</button>
          </div>
        </div>
      </div>
      <div className="bg-white/90 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_1px_0_rgba(15,23,42,0.04)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[84px] flex items-center justify-between gap-4">
        {/* Marke: Logo + TÜV-Prüfzeichen */}
        <div className="flex items-center gap-3 shrink-0">
          <Logo />
          <span className="hidden xl:block w-px h-11 bg-slate-200" aria-hidden="true" />
          <img src="/tuv-pruefzeichen-440.webp" alt="TÜV Rheinland geprüft – ID 0000046955" data-testid="header-tuv-badge" width="440" height="162" className="hidden xl:block h-11 lg:h-12 w-auto shrink-0" loading="eager" />
        </div>
        <nav className="hidden lg:flex items-center gap-0.5" aria-label="Hauptnavigation">
          {NAV.map((item) =>
            item.mega ? (
              <div key={item.to} className="relative group">
                <NavLink to={item.to} data-testid="nav-leistungen" className="flex items-center gap-1 px-1.5 py-2 text-[12.5px] font-semibold text-slate-700 hover:text-ember rounded-lg hover:bg-slate-100 transition-colors whitespace-nowrap">
                  {item.label} <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
                </NavLink>
                <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 translate-y-1 group-hover:translate-y-0" data-testid="mega-menu-leistungen">
                  <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 w-[820px] max-w-[calc(100vw-2rem)] grid grid-cols-3 gap-x-6 gap-y-5">
                    {SERVICE_GROUPS.map((g) => (
                      <div key={g.key}>
                        <p className="text-[11px] font-bold uppercase tracking-wider text-ember mb-2">{g.label}</p>
                        <div className="space-y-0.5">
                          {ALL_SERVICES.filter((s) => s.group === g.key).map((s) => (
                            <Link key={s.slug} to={`/${s.slug}`} className="block px-2 py-1.5 text-[13px] text-slate-700 rounded-lg hover:bg-slate-100 hover:text-ember transition-colors">{s.title}</Link>
                          ))}
                        </div>
                      </div>
                    ))}
                    <div className="col-span-3 border-t border-slate-100 pt-3">
                      <Link to="/leistungen/" className="inline-flex items-center gap-1.5 text-sm font-bold text-royal hover:text-ember transition-colors">Alle {ALL_SERVICES.length} Leistungen im Überblick →</Link>
                    </div>
                  </div>
                </div>
              </div>
            ) : item.dropdown ? (
              <div key={item.to} className="relative group">
                <NavLink to={item.to} data-testid={`nav-${item.label.toLowerCase()}`} className="flex items-center gap-1 px-1.5 py-2 text-[12.5px] font-semibold text-slate-700 hover:text-ember rounded-lg hover:bg-slate-100 transition-colors whitespace-nowrap">
                  {item.label} <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
                </NavLink>
                <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 translate-y-1 group-hover:translate-y-0">
                  <div className="bg-white rounded-xl shadow-xl border border-slate-200 p-2 w-64 max-h-[70vh] overflow-auto">
                    {item.dropdown.map((d) => (
                      <Link key={d.to} to={d.to} className="block px-3 py-2 text-sm text-slate-700 rounded-lg hover:bg-slate-100 hover:text-ember transition-colors">{d.label}</Link>
                    ))}
                    {item.to === "/standorte" && <Link to="/standorte" className="block px-3 py-2 text-sm font-semibold text-royal rounded-lg hover:bg-slate-100">Alle Standorte ansehen</Link>}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink key={item.to} to={item.to} data-testid={`nav-${item.label.toLowerCase().replace("ü", "u")}`} className="px-1.5 py-2 text-[12.5px] font-semibold text-slate-700 hover:text-ember rounded-lg hover:bg-slate-100 transition-colors whitespace-nowrap">
                {item.label}
              </NavLink>
            )
          )}
        </nav>
        <div className="hidden md:flex items-center gap-2.5 shrink-0">
          <a href={SITE.phoneHref} data-testid="header-phone-button" className="flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold text-navy border border-slate-300 hover:border-ember hover:text-ember transition-colors whitespace-nowrap shrink-0">
            <Phone size={16} /> <span className="hidden xl:inline">{SITE.phone}</span>
          </a>
          <button onClick={openRueckrufModal} data-testid="header-callback-button" className="cta-gradient px-4 py-2.5 rounded-full text-[13px] font-bold text-white whitespace-nowrap shrink-0">
            Rückruf
          </button>
        </div>
        <button className="lg:hidden p-2 text-navy" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menü öffnen">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 max-h-[75vh] overflow-auto" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {NAV.map((item) => {
              const hasSub = item.dropdown || item.mega;
              const isOpen = expanded === item.label;
              return (
                <div key={item.to} className="border-b border-slate-100 last:border-0 pb-1">
                  <div className="flex items-center justify-between gap-2">
                    <NavLink to={item.to} className="block flex-1 px-3 py-2.5 font-semibold text-navy rounded-lg hover:bg-slate-100">{item.label}</NavLink>
                    {hasSub && (
                      <button onClick={() => setExpanded(isOpen ? null : item.label)} data-testid={`mobile-nav-toggle-${item.label.toLowerCase()}`} aria-expanded={isOpen} aria-label={`${item.label} Untermenü ${isOpen ? "schließen" : "öffnen"}`} className="p-2.5 rounded-lg text-slate-500 hover:bg-slate-100">
                        <ChevronDown size={18} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      </button>
                    )}
                  </div>
                  {hasSub && isOpen && (
                    <div className="pl-4 pb-2" data-testid={`mobile-nav-panel-${item.label.toLowerCase()}`}>
                      {item.mega ? (
                        SERVICE_GROUPS.map((g) => (
                          <div key={g.key} className="mt-1">
                            <p className="px-3 pt-2 text-[10px] font-bold uppercase tracking-wider text-ember">{g.label}</p>
                            {ALL_SERVICES.filter((s) => s.group === g.key).map((s) => (
                              <Link key={s.slug} to={`/${s.slug}`} className="block px-3 py-1.5 text-sm text-slate-600 hover:text-ember">{s.title}</Link>
                            ))}
                          </div>
                        ))
                      ) : (
                        item.dropdown.map((d) => (
                          <Link key={d.to} to={d.to} className="block px-3 py-1.5 text-sm text-slate-600 hover:text-ember">{d.label}</Link>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <button onClick={() => { setOpen(false); openRueckrufModal(); }} data-testid="mobile-menu-callback-button" className="cta-gradient block w-full text-center mt-3 px-5 py-3 rounded-full font-bold text-white">Kostenlosen Rückruf anfordern</button>
            <div className="flex items-center justify-center gap-3 pt-4">
              <span className="text-xs text-slate-400 font-semibold">Folgen Sie uns:</span>
              <SocialLinks size={16} testidPrefix="mobile-menu" className="p-2.5 rounded-full bg-slate-100 text-navy hover:bg-ember hover:text-white transition-colors" />
            </div>
          </div>
        </div>
      )}
      </div>
    </header>
  );
};

const FOOTER_LEISTUNGEN = ["unfallgutachten", "kfz-gutachten", "haftpflichtgutachten", "kasko-gutachten", "lkw-gutachten", "kostenvoranschlag", "kfz-wertgutachten", "kurzgutachten", "oldtimer-gutachten", "e-auto-gutachten"];

const FooterCol = ({ title, children, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testid} className="border-b border-white/10 lg:border-0">
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="lg:hidden w-full flex items-center justify-between py-4 text-white font-display text-base font-bold">
        {title}
        <ChevronDown size={18} className={`text-ember transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <h3 className="hidden lg:block text-white font-display text-lg font-bold mb-3">{title}</h3>
      <div className={`${open ? "block" : "hidden"} lg:block pb-4 lg:pb-0`}>
        {children}
      </div>
    </div>
  );
};

const FooterLinks = ({ items }) => (
  <ul className="space-y-2 text-sm pb-4 lg:pb-0">
    {items.map((i) => (
      <li key={i.to + i.label}>
        <Link className={`hover:text-ember transition-colors ${i.ember ? "font-semibold text-ember" : ""}`} to={i.to}>{i.label}</Link>
      </li>
    ))}
  </ul>
);

export const FooterNewsletter = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Bitte eine gültige E-Mail-Adresse eingeben");
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setDone(true);
    } catch {
      toast.error("Anmeldung fehlgeschlagen – bitte später erneut versuchen");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="py-7 border-b border-white/10" data-testid="footer-newsletter">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="md:max-w-sm">
          <p className="font-display text-lg font-bold text-white">Unfall-Wissen per E-Mail</p>
          <p className="text-xs text-slate-400 mt-1 leading-relaxed">Gelegentlich praktische Tipps zu Unfall, Gutachten und Ihren Ansprüchen. Anmeldung mit Bestätigungs-E-Mail, jederzeit abbestellbar.</p>
        </div>
        {done ? (
          <p className="text-sm text-emerald-400 font-semibold flex items-center gap-2" data-testid="footer-newsletter-success"><BadgeCheck size={17} /> Fast geschafft – bitte bestätigen Sie den Link in Ihrer E-Mail.</p>
        ) : (
          <form onSubmit={submit} className="flex flex-1 gap-2 max-w-xl">
            <label htmlFor="footer-newsletter-email" className="sr-only">E-Mail-Adresse für den Newsletter</label>
            <input id="footer-newsletter-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Ihre E-Mail-Adresse" data-testid="footer-newsletter-input" className="flex-1 min-w-0 rounded-full bg-white/10 border border-white/15 px-4 py-2.5 text-sm text-white placeholder:text-slate-400 outline-none focus:border-ember" />
            <button type="submit" disabled={busy} data-testid="footer-newsletter-submit" className="cta-gradient shrink-0 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={14} />} Anmelden
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export const Footer = () => (
  <footer className="bg-navy text-slate-300 relative grain" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-8">
      {/* Markenzeile */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-6 pb-9 border-b border-white/10">
        <Logo light />
        <div className="sm:border-l sm:border-white/10 sm:pl-6">
          <p className="text-sm text-slate-300 max-w-md leading-relaxed">Unabhängige, TÜV-zertifizierte KFZ-Sachverständige – wir kommen zu Ihnen. Köln, Krefeld, Moers & ganz NRW.</p>
          <div className="flex gap-2.5 mt-3">
            <SocialLinks size={17} testidPrefix="footer" className="p-2 rounded-full bg-white/10 hover:bg-ember transition-colors" />
          </div>
        </div>
        <div className="sm:ml-auto bg-white rounded-xl p-2.5 self-start">
          <img src="/tuv-pruefzeichen-440.webp" alt="TÜV Rheinland geprüft – ID 0000046955" width="440" height="162" className="h-14 w-auto" loading="lazy" />
        </div>
      </div>

      <FooterNewsletter />

      {/* Linkspalten: mobil als Akkordeon, Desktop als Raster */}
      <div className="lg:grid lg:grid-cols-6 lg:gap-8 py-2 lg:py-9">
        {[
          {
            title: "Leistungen", testid: "footer-col-leistungen",
            items: [
              ...SERVICE_LINKS.filter((s) => FOOTER_LEISTUNGEN.includes(s.slug)).map((s) => ({ to: `/${s.slug}`, label: s.title })),
              { to: "/leistungen/", label: "Alle Leistungen →", ember: true },
            ],
          },
          {
            title: "Wissen", testid: "footer-col-wissen",
            items: [
              { to: "/ihr-recht", label: "Ihr Recht" },
              { to: "/faq", label: "Häufige Fragen" },
              { to: "/glossar", label: "Glossar" },
              { to: "/ratgeber/", label: "Ratgeber" },
              { to: "/schadenarten", label: "Schadenarten" },
              { to: "/unfall-was-tun", label: "Unfall – was tun?" },
              { to: "/downloads", label: "Downloads" },
            ],
          },
          {
            title: "Rechner & Service", testid: "footer-col-rechner",
            items: [
              { to: "/rechner", label: "KFZ-Rechner" },
              { to: "/online-kostenvoranschlag", label: "Online-Kostenvoranschlag" },
              { to: "/unfall-ersteinschaetzung", label: "Unfall-Ersteinschätzung" },
              { to: "/anspruch-pruefen", label: "Anspruch prüfen" },
              { to: "/unfall-assistent", label: "Unfall-Assistent" },
              { to: "/downloads", label: "Downloads" },
            ],
          },
          {
            title: "Unternehmen", testid: "footer-col-unternehmen",
            items: [
              { to: "/ueber-uns/", label: "Über uns" },
              { to: "/team", label: "Team" },
              { to: "/qualitaet", label: "Qualität & Standards" },
              { to: "/technik", label: "Technik & Ausstattung" },
              { to: "/karriere", label: "Karriere & Jobs" },
              { to: "/freunde-empfehlen", label: "Freunde empfehlen" },
              { to: "/kontakt", label: "Kontakt" },
            ],
          },
          {
            title: "Entdecken", testid: "footer-col-entdecken",
            items: [
              { to: "/automarken/", label: "Automarken" },
              { to: "/unfall", label: "Unfallthemen" },
              { to: "/rechner", label: "KFZ-Rechner" },
              { to: "/standorte", label: "Standorte" },
              { to: "/downloads", label: "Downloads" },
              { to: "/freunde-empfehlen", label: "Freunde empfehlen (50 €)" },
            ],
          },
          {
            title: "Ratgeber", testid: "footer-col-ratgeber",
            items: [
              { to: "/frontalzusammenstoss-frontalunfall", label: "Frontalzusammenstoß" },
              { to: "/auffahrunfall-schuld-bei-unfall", label: "Auffahrunfall – Schuld" },
              { to: "/hagelschaden-reparatur-und-versicherungsschutz", label: "Hagelschaden" },
              { to: "/fiktive-abrechnung-bei-unfallschaden", label: "Fiktive Abrechnung" },
              { to: "/bagatellschaden-am-auto", label: "Bagatellschaden" },
              { to: "/parkschaden-am-auto", label: "Parkschaden" },
              { to: "/wirtschaftlicher-totalschaden", label: "Totalschaden" },
              { to: "/unfall-heckschaden", label: "Heckschaden" },
              { to: "/haftpflichtschaden-auto", label: "Haftpflichtschaden" },
              { to: "/spurwechsel-schuld-bei-unfall", label: "Spurwechsel – Schuld" },
            ],
          },
          {
            title: "Gutachten-Wissen", testid: "footer-col-gutachten-wissen",
            items: [
              { to: "/wer-zahlt-den-kfz-gutachter", label: "Wer zahlt den Gutachter?" },
              { to: "/freie-gutachterwahl", label: "Freie Gutachterwahl" },
              { to: "/kfz-gutachter-kosten", label: "Gutachter-Kosten" },
              { to: "/schaden-melden", label: "Schaden melden" },
              { to: "/kfz-gutachter-in-der-naehe", label: "Gutachter in der Nähe" },
              { to: "/auto-begutachtung", label: "Auto-Begutachtung" },
              { to: "/kfz-gutachter-und-kfz-sachverstaendiger", label: "Gutachter & Sachverständiger" },
              { to: "/unfallrekonstruktion", label: "Unfallrekonstruktion" },
              { to: "/kfz-sachverstaendiger-franchise", label: "Franchise" },
              { to: "/vertriebspartner", label: "Vertriebspartner" },
            ],
          },
        ].map((col) => (
          <FooterCol key={col.title} title={col.title} testid={col.testid}>
            <FooterLinks items={col.items} />
          </FooterCol>
        ))}
        {/* Kontakt-Spalte */}
        <div data-testid="footer-col-kontakt" className="border-b border-white/10 lg:border-0">
          <h3 className="text-white font-display text-lg font-bold py-4 lg:py-0 lg:mb-3">Kontakt</h3>
          <div className="pb-4 lg:pb-0 text-sm space-y-3.5">
            <div className="flex gap-2.5">
              <MapPin size={16} className="shrink-0 mt-0.5 text-ember" />
              <span>{SITE.addressKoeln}<br /><a href={SITE.phoneHref} className="text-white font-semibold hover:text-ember">{SITE.phone}</a></span>
            </div>
            <div className="flex gap-2.5">
              <MapPin size={16} className="shrink-0 mt-0.5 text-ember" />
              <span>{SITE.addressKrefeld}<br /><a href={SITE.phoneKrefeldHref} className="text-white font-semibold hover:text-ember">{SITE.phoneKrefeld}</a></span>
            </div>
            <div className="pt-1 space-y-2 text-sm">
              <p><Link className="hover:text-ember" to="/kontakt">Kontakt & Anfahrt</Link></p>
              <p><Link className="hover:text-ember" to="/freunde-empfehlen">Freunde empfehlen (50 €)</Link></p>
              <p><Link className="hover:text-ember" to="/karriere">Karriere & Jobs</Link></p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-400 items-center justify-between">
        <span>© {new Date().getFullYear()} UnfallExpert24 – Alle Rechte vorbehalten.</span>
        <div className="flex gap-5 pr-20 sm:pr-24">
          <Link to="/impressum" className="hover:text-ember">Impressum</Link>
          <Link to="/datenschutzerklaerung" className="hover:text-ember">Datenschutzerklärung</Link>
          <Link to="/cookies" className="hover:text-ember">Cookies</Link>
          <button type="button" onClick={() => window.__openCookieSettings && window.__openCookieSettings()} data-testid="footer-cookie-settings" className="hover:text-ember">Cookie-Einstellungen</button>
          <Link to="/admin" data-testid="footer-admin-login" className="hover:text-ember flex items-center gap-1"><Lock size={11} /> Admin-Login</Link>
        </div>
      </div>
    </div>
    <div className="h-16 md:hidden" />
  </footer>
);

export const StickyMobileBar = () => {
  const { pathname } = useLocation();
  // Krefeld-Nummer auf Krefeld-/Moers-Seiten, sonst Köln (Aufgabe 9)
  const krefeldArea = /kfz-gutachter-(krefeld|moers)/.test(pathname);
  const phoneDisplay = krefeldArea ? SITE.phoneKrefeld : "0221 67029067";
  const phoneHref = krefeldArea ? SITE.phoneKrefeldHref : "tel:+4922167029067";
  return (
  <div className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-navy/95 backdrop-blur-lg border-t border-white/10" data-testid="sticky-mobile-bar">
    <p className="text-center text-[10px] font-semibold text-slate-300 pt-1.5">Gerade einen Unfall gehabt? Wir helfen sofort am Telefon.</p>
    <div className="grid grid-cols-3">
      <a href={phoneHref} data-testid="sticky-mobile-call-button" className="flex flex-col items-center gap-1 py-2 text-white text-[11px] font-semibold">
        <Phone size={20} /> Anrufen
      </a>
      <a href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hallo, ich hatte gerade einen Unfall und brauche Hilfe.")}`} target="_blank" rel="noopener noreferrer" data-testid="sticky-mobile-whatsapp-button" className="flex flex-col items-center gap-1 py-2 text-white text-[11px] font-semibold border-x border-white/10">
        <MessageCircle size={20} /> WhatsApp
      </a>
      <button onClick={openRueckrufModal} data-testid="sticky-mobile-callback-button" className="cta-gradient flex flex-col items-center gap-1 py-2 text-white text-[11px] font-bold">
        <PhoneOutgoing size={20} /> Rückruf
      </button>
    </div>
  </div>
  );
};

export const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

export const RueckrufModal = ({ onClose }) => {
  const [form, setForm] = useState({ name: "", telefon: "", schuld: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    if (!form.schuld) { toast.error("Bitte beantworten Sie die Schuldfrage."); return; }
    setLoading(true);
    try {
      const parts = form.name.trim().split(/\s+/);
      const attr = getAttribution();
      const fd = new FormData();
      fd.append("vorname", parts[0] || form.name);
      fd.append("nachname", parts.slice(1).join(" "));
      fd.append("telefon", form.telefon);
      fd.append("schuldfrage", form.schuld);
      fd.append("quelle", "rueckruf-button");
      fd.append("quell_url", window.location.pathname);
      ["utm_source", "utm_medium", "utm_campaign", "gclid", "gbraid", "wbraid"].forEach((k) => fd.append(k, attr[k] || ""));
      fd.append("landing_page", attr.landing_page || "");
      await axios.post(`${API}/leads/schaden`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      trackMetaLead("rueckruf");
      const phoneHash = await sha256Hex(form.telefon);
      dl({ event: "lead_form_submit", form_step: "complete", lead_type: "unfall", page_path: window.location.pathname, user_data: { phone_number: phoneHash || undefined } });
      toast.success("Alles klar – wir rufen Sie schnellstmöglich zurück!");
      onClose();
      navigate("/danke");
    } catch {
      toast.error(`Senden fehlgeschlagen. Bitte rufen Sie direkt an: ${SITE.phone}`);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-navy/70 backdrop-blur-sm" data-testid="rueckruf-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 sm:p-7 w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl font-bold text-navy">Wir rufen Sie zurück</h2>
            <p className="text-sm text-slate-500 mt-1">Kostenlos & unverbindlich – {PROMISES.rueckruf}.</p>
          </div>
          <button onClick={onClose} data-testid="rueckruf-modal-close" aria-label="Schließen" className="p-2 rounded-full hover:bg-slate-100 text-slate-500"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="mt-5 space-y-3.5">
          <div>
            <label htmlFor="rr-name" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Ihr Name *</label>
            <input id="rr-name" required data-testid="rueckruf-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-navy focus:border-ember focus:ring-2 focus:ring-ember/20 transition-shadow" placeholder="Vor- und Nachname" autoComplete="name" />
          </div>
          <div>
            <label htmlFor="rr-tel" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Ihre Telefonnummer *</label>
            <input id="rr-tel" required type="tel" data-testid="rueckruf-phone-input" value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-navy focus:border-ember focus:ring-2 focus:ring-ember/20 transition-shadow" placeholder="z. B. 0171 2345678" autoComplete="tel" />
          </div>
          <fieldset>
            <legend className="text-xs font-semibold uppercase tracking-wider text-slate-500">War der Unfall unverschuldet? *</legend>
            <div className="grid grid-cols-3 gap-2 mt-1.5">
              {[["nein", "Ja"], ["teilweise", "Teilweise"], ["unklar", "Weiß nicht"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setForm({ ...form, schuld: v })} data-testid={`rueckruf-schuld-${v}`}
                  className={`rounded-lg border-2 px-2 py-2 text-xs font-bold transition-colors ${form.schuld === v ? "border-ember bg-ember/5 text-ember" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                  {l}
                </button>
              ))}
            </div>
          </fieldset>
          <button type="submit" disabled={loading} data-testid="rueckruf-submit-button" className="cta-gradient w-full flex items-center justify-center gap-2 rounded-lg px-5 py-3.5 font-bold text-white disabled:opacity-60">
            {loading ? <Loader2 className="animate-spin" size={18} /> : <PhoneOutgoing size={18} />} Kostenlosen Rückruf anfordern
          </button>
          <p className="text-[11px] text-slate-400 leading-snug">Mit dem Absenden akzeptieren Sie unsere <Link to="/datenschutzerklaerung" className="underline">Datenschutzerklärung</Link>.</p>
        </form>
      </div>
    </div>
  );
};

export const FloatingContact = () => {
  const [open, setOpen] = useState(false);
  const actions = [
    { key: "anruf", label: "Jetzt anrufen", icon: PhoneCall, href: "tel:+4922167029067", cls: "bg-royal text-white", testid: "floating-call-button" },
    { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, href: `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(SITE.whatsappText)}`, external: true, cls: "bg-emerald-500 text-white", testid: "floating-whatsapp-button" },
    { key: "rueckruf", label: "Rückruf anfordern", icon: PhoneOutgoing, onClick: () => { setOpen(false); openRueckrufModal(); }, cls: "bg-white text-navy border border-slate-200", testid: "floating-callback-button" },
  ];
  return (
    <>
      <div className="fixed right-4 bottom-28 md:bottom-6 z-40 flex flex-col items-end gap-3" data-testid="floating-contact">
        {actions.map((a, i) => (
          <div key={a.key} className={`flex items-center gap-2.5 transition-all duration-300 ${open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"}`} style={{ transitionDelay: open ? `${i * 60}ms` : "0ms" }}>
            <span className="bg-navy text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg whitespace-nowrap">{a.label}</span>
            {a.href ? (
              <a href={a.href} {...(a.external ? { target: "_blank", rel: "noopener noreferrer" } : {})} data-testid={a.testid} aria-label={a.label} className={`flex items-center justify-center w-12 h-12 rounded-full shadow-xl transition-transform hover:scale-110 ${a.cls}`}>
                <a.icon size={21} />
              </a>
            ) : (
              <button onClick={a.onClick} data-testid={a.testid} aria-label={a.label} className={`flex items-center justify-center w-12 h-12 rounded-full shadow-xl transition-transform hover:scale-110 ${a.cls}`}>
                <a.icon size={21} />
              </button>
            )}
          </div>
        ))}
        <button
          onClick={() => setOpen(!open)}
          data-testid="floating-contact-toggle"
          aria-label={open ? "Kontaktmenü schließen" : "Kontaktoptionen öffnen"}
          className="relative flex items-center justify-center w-14 h-14 rounded-full cta-gradient text-white shadow-xl transition-transform duration-300 hover:scale-110"
        >
          {!open && <span className="absolute inset-0 rounded-full bg-ember animate-ping opacity-25" />}
          <span className={`relative transition-transform duration-300 ${open ? "rotate-45" : ""}`}>{open ? <X size={24} /> : <Phone size={24} />}</span>
        </button>
      </div>
    </>
  );
};

export const Layout = ({ children }) => (
  <div className="min-h-screen flex flex-col">
    <Header />
    <main className="flex-1 pt-[72px] md:pt-[108px]">{children}</main>
    <Footer />
    <FloatingContact />
    <StickyMobileBar />
    <GlobalRueckruf />
  </div>
);
